import { timeFormat } from 'd3-time-format';
import { timeMonth } from 'd3-time';

import './styles/graphic.scss';

function fmtTickDate(date) {
  const full = ['March', 'April', 'May', 'June', 'July'];
  const monthFull = timeFormat('%B')(date);
  const month = full.includes(monthFull) ? monthFull : `${timeFormat('%b')(date)}.`;
  return `${month} '${timeFormat('%y')(date)}`;
}

function fmtAnnotDate(date) {
  const full = ['March', 'April', 'May', 'June', 'July'];
  const monthFull = timeFormat('%B')(date);
  const month = full.includes(monthFull) ? monthFull : `${timeFormat('%b')(date)}.`;
  return `${month.replace('Sep.', 'Sept.')} ${timeFormat('%-d, %Y')(date)}`;
}

const fmtTooltipDate = timeFormat('%B %-d, %Y');
// const fmtTickDate = timeFormat("%b. '%y");
// const fmtAnnotDate = timeFormat('%b. %-d, %Y');

const annotColor = '#444444';

function wrapText(textEl, maxWidth) {
  const words = textEl.text().split(/\s+/).reverse();
  const x = textEl.attr('x');
  const y = textEl.attr('y');
  textEl.text(null);
  let line = [];
  let tspan = textEl.append('tspan').attr('x', x).attr('y', y);
  while (words.length) {
    const word = words.pop();
    line.push(word);
    tspan.text(line.join(' '));
    if (tspan.node().getComputedTextLength() > maxWidth) {
      line.pop();
      tspan.text(line.join(' '));
      line = [word];
      tspan = textEl.append('tspan').attr('x', x).attr('dy', '1.2em').text(word);
    }
  }
}

// Appends wrapped tspans to parentEl, each with dy='1.2em' (used after a bold date tspan).
function wrapTextContinued(parentEl, labelText, x, maxWidth) {
  const words = labelText.split(/\s+/).reverse();
  let line = [];
  let tspan = parentEl.append('tspan').attr('x', x).attr('dy', '1.2em');
  while (words.length) {
    const word = words.pop();
    line.push(word);
    tspan.text(line.join(' '));
    if (tspan.node().getComputedTextLength() > maxWidth) {
      line.pop();
      tspan.text(line.join(' '));
      line = [word];
      tspan = parentEl.append('tspan').attr('x', x).attr('dy', '1.2em').text(word);
    }
  }
}

function tooltipPosition(event, containerNode) {
  const rect = containerNode.getBoundingClientRect();
  return [event.clientX - rect.left + 12, event.clientY - rect.top - 36];
}

export default function (props, container, isMobile) {
  const {
    israelData,
    hezbollahData,
    israelY,
    israelYAxis,
    israelHeight,
    israelYMax,
    hezbollahY,
    hezbollahYAxis,
    hezbollahHeight,
    hezbollahYMax,
    x,
    xAxis,
    width,
    margins,
    barWidth,
    annotations,
  } = props;

  const tickInterval = isMobile ? timeMonth.every(12) : timeMonth.every(3);
  const annotHeaderHeight = isMobile ? 50 : 180;

  // Chronological ordering for numbered annotations — order is by date, not config order.
  const annotsSortedByDate = annotations.slice().sort((a, b) => a.date - b.date);
  const annotNumberMap = new Map(annotsSortedByDate.map((ann, i) => [ann, i + 1]));

  // Shared tooltip element
  // const tooltip = container.append('div')
  //   .attr('id', 'tooltip');

  const svgWidth = width + margins.left + margins.right;

  container.style('position', 'relative');

  // --- Annotation header: labels above both facets (desktop only) ---
  // On mobile, annotations are replaced by in-chart numbered markers + a key below.
  if (!isMobile) {
    const headerDiv = container.append('div')
      .attr('class', 'annotation-header');

    const headerSvg = headerDiv.append('svg')
      .attr('width', svgWidth)
      .attr('height', annotHeaderHeight);

    const headerG = headerSvg.append('g')
      .attr('transform', `translate(${margins.left}, 0)`);

    annotations.forEach((ann) => {
      const ax = x(ann.date);
      const labelYPos = ann.labelY !== undefined ? ann.labelY : 12;
      // const labelYPos = isMobile ? ann.labelYMobile !== undefined ? ann.labelYMobile : 75 : ann.labelY !== undefined ? ann.labelY : 12;
      // const labelYPos = isMobile ? ann.labelYMobile : ann.labelY;
      const onLeft = ann.labelSide === 'left';
      const textX = onLeft ? ax - 6 : ax + 6;

      if (ann.label) {
        const annotText = headerG.append('text')
          .attr('class', 'annotation-label')
          .attr('x', textX)
          .attr('y', labelYPos)
          .attr('text-anchor', onLeft ? 'end' : 'start');

        // Bullet + date: bold, colored
        annotText.append('tspan')
          .attr('font-weight', 'bold')
          .attr('fill', annotColor)
          .text(`${fmtAnnotDate(ann.date)}`);

        // Label text wraps on subsequent lines
        if (ann.labelWidth) {
          wrapTextContinued(annotText, ann.label, textX, ann.labelWidth);
        } else {
          annotText.append('tspan')
            .attr('x', textX)
            .attr('dy', '1.2em')
            .text(ann.label);
        }
      }
    });
  }

  // --- Mobile annotation circle-number header (above both facets, appears once) ---
  const circleR = 9;

  if (isMobile) {
    const mobileHeaderDiv = container.append('div')
      .attr('class', 'annotation-header');

    const mobileHeaderSvg = mobileHeaderDiv.append('svg')
      .attr('width', svgWidth)
      .attr('height', annotHeaderHeight);

    const mobileHeaderG = mobileHeaderSvg.append('g')
      .attr('transform', `translate(${margins.left}, 0)`);

    annotations.forEach((ann) => {
      const ax = x(ann.date);
      // const cy = circleR + 2;
      const cy = ann.labelYMobile !== undefined ? ann.labelYMobile : circleR + 2;

      // Filled circle
      mobileHeaderG.append('circle')
        .attr('cx', ax).attr('cy', cy)
        .attr('r', circleR)
        .attr('stroke', '#444444')
        .attr('fill', '#ffffff');

      // White number centered on circle
      mobileHeaderG.append('text')
        .attr('x', ax).attr('y', cy)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('fill', annotColor)
        .attr('font-size', '10px')
        .attr('font-weight', '700')
        .attr('pointer-events', 'none')
        .text(annotNumberMap.get(ann));
    });
  }

  // --- Facets ---
  const facets = [
    {
      label: 'Israel',
      data: israelData,
      color: '#7C4EA5',
      y: israelY,
      yAxis: israelYAxis,
      height: israelHeight,
      tickValues: Array.from({ length: israelYMax / 50 + 1 }, (_, i) => i * 50),
      showXAxis: false,
    },
    {
      label: 'Hezbollah',
      data: hezbollahData,
      color: '#F18851',
      y: hezbollahY,
      yAxis: hezbollahYAxis,
      height: hezbollahHeight,
      tickValues: Array.from({ length: hezbollahYMax / 50 + 1 }, (_, i) => i * 50),
      showXAxis: true,
    },
  ];

  facets.forEach(({
    label, data, color, y, yAxis, height, tickValues, showXAxis,
  }) => {
    const facet = container.append('div')
      .attr('class', 'facet');

    facet.append('h3')
      .attr('class', 'facet-title')
      .style('background-color', color)
      .style('color', '#ffffff')
      .text(label);

    const bottomMargin = showXAxis ? margins.bottom : 8;
    const svgHeight = height + margins.top + bottomMargin;

    const svg = facet.append('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .attr('aria-label', `${label} strikes over time`)
      .style('overflow', 'visible');

    const g = svg.append('g')
      .attr('transform', `translate(${margins.left}, ${margins.top})`);

    // y gridlines
    g.append('g')
      .attr('class', 'y grid')
      .call(
        yAxis(y)
          .tickValues(tickValues)
          .tickSize(-width)
          .tickFormat(d => d),
      )
      .selectAll('text')
      .attr('x', -5);

    g.select('.y.grid')
      .selectAll('.tick')
      .filter(d => d === 0)
      .select('line')
      .style('stroke', showXAxis ? '#888888' : '#888888')
      .style('stroke-width', showXAxis ? '1px' : '1px');

    // g.append('line')
    //   .attr('class', 'zero-line')
    //   .attr('x1', 0).attr('x2', width)
    //   .attr('y1', height)
    //   .attr('y2', height)
    //   .attr('stroke', showXAxis ? '#eeeeee' : '#eeeeee')
    //   .attr('stroke-width', showXAxis ? 0.2 : 0.1);
    // Remove zero tick label
    // g.select('.y.grid')
    //   .selectAll('.tick')
    //   .filter(d => d === 0)
    //   .select('text')
    //   .remove();

    // x axis on bottom facet only
    if (showXAxis) {
      g.append('g')
        .attr('class', 'x grid')
        .attr('transform', `translate(0, ${height})`)
        .call(
          xAxis(x)
            .ticks(tickInterval)
            .tickFormat(fmtTickDate),
        );
    }

    // Annotation lines through facet (labels are in the header above both facets).
    // Lines extend from above the top margin (y1 = -margins.top) so they appear
    // continuous with the header lines when the SVG overflow is visible.
    const annotG = g.append('g').attr('class', 'annotations');

    annotations.forEach((ann) => {
      const ax = x(ann.date);

      if (ann.type === 'span' && ann.endDate) {
        const ax2 = x(ann.endDate);
        annotG.append('rect')
          .attr('class', 'annotation-span')
          .attr('x', ax)
          .attr('y', 0)
          .attr('width', Math.max(ax2 - ax + barWidth, barWidth))
          .attr('height', height);
      }

      // Labels are now rendered in the annotation header above both facets.
      // Uncomment the block below to switch back to per-facet inline labels.
      // if (ann.label) {
      //   const onLeft = ann.labelSide === 'left';
      //   const annotText = annotG.append('text')
      //     .attr('class', 'annotation-label')
      //     .attr('x', onLeft ? ax - 6 : ax + 6)
      //     .attr('y', ann.labelY || 12)
      //     .attr('text-anchor', onLeft ? 'end' : 'start')
      //     .text(ann.label);
      //   if (ann.labelWidth) wrapText(annotText, ann.labelWidth);
      // }
    });

    // Bars
    g.append('g')
      .attr('class', 'bars')
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('fill', color)
      .attr('x', d => x(d.date) - barWidth / 2)
      .attr('y', d => y(d.count))
      .attr('width', barWidth)
      .attr('height', d => Math.max(0, height - y(d.count)));
    // .on('mouseover', (event, d) => {
    //   tooltip
    //     .classed('visible', true)
    //     .html(`<strong>${fmtTooltipDate(d.date)}</strong><br>${d.count} strikes`);
    //   const [tx, ty] = tooltipPosition(event, container.node());
    //   tooltip.style('left', `${tx}px`).style('top', `${ty}px`);
    // })
    // .on('mousemove', (event) => {
    //   const [tx, ty] = tooltipPosition(event, container.node());
    //   tooltip.style('left', `${tx}px`).style('top', `${ty}px`);
    // })
    // .on('mouseout', () => {
    //   tooltip.classed('visible', false);
    // });
  });

  // --- Annotation line overlay: single continuous lines from chart top to the x-axis ---
  const contentHeight = container.node().offsetHeight;

  // Find the x-axis position in container-relative coordinates.
  // The x-axis sits at margins.top + hezbollahHeight inside the last facet's SVG.
  const containerTop = container.node().getBoundingClientRect().top;
  const facetSvgs = container.selectAll('.facet svg').nodes();
  const lastSvgTop = facetSvgs[facetSvgs.length - 1].getBoundingClientRect().top;
  const xAxisY = (lastSvgTop - containerTop) + margins.top + hezbollahHeight;

  const overlaySvg = container.append('svg')
    .attr('class', 'annotation-overlay')
    .attr('width', svgWidth)
    .attr('height', contentHeight)
    .style('position', 'absolute')
    .style('top', '0')
    .style('left', '0')
    .style('pointer-events', 'none');

  const overlayG = overlaySvg.append('g')
    .attr('transform', `translate(${margins.left}, 0)`);

  annotations.forEach((ann) => {
    overlayG.append('line')
      .attr('class', 'annotation-line')
      .attr('x1', x(ann.date)).attr('x2', x(ann.date))
      .attr('y1', isMobile
        ? (ann.labelYMobile !== undefined ? ann.labelYMobile : circleR + 2) + circleR
        : (ann.labelY !== undefined ? ann.labelY : 12))
      .attr('y2', xAxisY);
  });

  // --- Mobile annotation key: numbered list below both facets ---
  // Annotations are listed in chronological date order to match the line numbers.
  if (isMobile) {
    const key = container.append('div').attr('class', 'annotation-key');
    annotsSortedByDate.forEach((ann, i) => {
      const item = key.append('div').attr('class', 'annotation-key-item');
      item.append('span').attr('class', 'annotation-key-num').text(`${i + 1} `);
      // item.append('span').attr('class', 'annotation-key-label')
      //   .text(`${fmtAnnotDate(ann.date)}`);
      item.append('span').attr('class', 'annotation-key-label').html(`<b>${fmtAnnotDate(ann.date)}</b> ${ann.label}`);
    });
  }
}
